import { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

/**
 * YouTube Player - tries direct autoplay embed first, falls back to proxy
 */
const YouTubePlayer = ({ 
  videoId, 
  title, 
  thumbnail, 
  className = "w-full h-full" 
}) => {
  const [playerState, setPlayerState] = useState('loading');
  const [isMuted, setIsMuted] = useState(true);
  const [showUnmuteHint, setShowUnmuteHint] = useState(true);
  const iframeRef = useRef(null);

  // Direct YouTube embed with autoplay (muted for browser policy)
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=1&modestbranding=1&playsinline=1&rel=0`;

  // Toggle mute - reload iframe with unmuted URL
  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    setShowUnmuteHint(false);
    
    // Update iframe src with new mute state
    if (iframeRef.current) {
      const newUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${newMuted ? '1' : '0'}&controls=1&modestbranding=1&playsinline=1&rel=0`;
      iframeRef.current.src = newUrl;
    }
  }, [isMuted, videoId]);

  useEffect(() => {
    setPlayerState('loading');
    
    // Assume playing after short delay (iframe load event unreliable for cross-origin)
    const timer = setTimeout(() => {
      setPlayerState('playing');
    }, 1500);

    return () => clearTimeout(timer);
  }, [videoId]);

  // Unmute hint - centered
  const UnmuteHint = () => (
    <button
      onClick={toggleMute}
      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30 flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-6 py-4 rounded-xl transition-all shadow-2xl animate-pulse"
      data-testid="unmute-hint"
    >
      <VolumeX className="w-8 h-8" />
      <div className="text-left">
        <span className="text-lg font-bold block">Tap to Unmute</span>
        <span className="text-sm opacity-80">Sound is muted</span>
      </div>
    </button>
  );

  // Mute toggle
  const MuteToggle = () => (
    <button
      onClick={toggleMute}
      className="absolute bottom-4 right-4 z-30 w-12 h-12 flex items-center justify-center bg-black/70 hover:bg-black/90 text-white rounded-full transition shadow-lg"
      data-testid="mute-toggle"
    >
      {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
    </button>
  );

  // YouTube link
  const WatchOnYouTube = () => (
    <a 
      href={`https://www.youtube.com/watch?v=${videoId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="absolute top-4 left-4 z-30 flex items-center gap-2 bg-red-600/90 hover:bg-red-600 text-white px-3 py-1.5 rounded-full text-sm font-medium transition shadow-lg"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
      YouTube
    </a>
  );

  // Loading overlay
  const LoadingOverlay = () => (
    <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/80 pointer-events-none">
      {thumbnail && (
        <img src={thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30"/>
      )}
      <div className="text-center">
        <div className="animate-spin h-12 w-12 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-white text-sm">Loading video...</p>
      </div>
    </div>
  );

  return (
    <div className={`${className} relative bg-black overflow-hidden`} data-testid="youtube-player">
      {/* YouTube iframe with autoplay */}
      <iframe
        ref={iframeRef}
        className="w-full h-full absolute inset-0"
        src={embedUrl}
        title={title || 'YouTube Video'}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
      
      {/* Loading overlay */}
      {playerState === 'loading' && <LoadingOverlay />}
      
      {/* Controls */}
      {playerState === 'playing' && (
        <>
          <WatchOnYouTube />
          {isMuted && showUnmuteHint && <UnmuteHint />}
          <MuteToggle />
        </>
      )}
    </div>
  );
};

export default YouTubePlayer;
